import React, { useState, useEffect, useCallback, useRef } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Button from "@/components/ui/button";
import Popover from "@/components/ui/popover";
import Chip from "@/components/ui/chip";
import FileImage from "@/components/ui/file-image";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type {
  AnnouncementEntry,
  AnnouncementStatus,
} from "../types/announcements.types";
import type { AnnouncementFormValues } from "../types/announcement-form.types";
import { announcementsApi, uiStatusToApi } from "../services/announcements.api";
import { formDateToISOStart, formDateToISOEnd, parseDateRangeToISO } from "../utils/dateUtils";
import CreateAnnouncementDrawer, {
  TARGET_AUDIENCE_ALL,
  type EditAnnouncementContext,
} from "../components/CreateAnnouncementDrawer";
import AnnouncementDetailDrawer from "../components/AnnouncementDetailDrawer";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./AnnouncementsPage.module.scss";

const STATUS_LABELS: Record<AnnouncementStatus, string> = {
  yayinda: i18n.t("announcementsStatus.yayinda"),
  kaldirildi: i18n.t("announcementsStatus.kaldirildi"),
  taslak: "Taslak",
  suresi_dolmus: i18n.t("announcementsStatus.suresi_dolmus"),
};

const STATUS_CHIP_TYPE: Record<
  AnnouncementStatus,
  "info" | "error" | "warning"
> = {
  yayinda: "info",
  kaldirildi: "error",
  taslak: "warning",
  suresi_dolmus: "error",
};

const SEARCH_DEBOUNCE_MS = 400;

const AnnouncementsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canView = hasPermission(PERMISSIONS.ANNOUNCEMENTS_VIEW);
  const canCreate = hasPermission(PERMISSIONS.ANNOUNCEMENTS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.ANNOUNCEMENTS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.ANNOUNCEMENTS_DELETE);

  if (!canView) {
    return <Navigate to="/dashboard/unauthorized" replace />;
  }

  const [entries, setEntries] = useState<AnnouncementEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<AnnouncementEntry[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const [filterValues, setFilterValues] = useState<
    Record<string, AnnouncementStatus | AnnouncementStatus[] | string | string[] | [Date | null, Date | null]>
  >({});
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [viewDrawerId, setViewDrawerId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AnnouncementEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkUnpublishing, setIsBulkUnpublishing] = useState(false);
  const [sortBy, setSortBy] = useState<
    "createdAt" | "startDate" | "title" | "status"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const statusFilter = filterValues.status;
      const validStatuses = ["taslak", "yayinda", "kaldirildi"] as const;
      const statusVal = Array.isArray(statusFilter) ? statusFilter[0] : statusFilter;
      const statusApi =
        statusVal != null &&
        statusVal !== "" &&
        validStatuses.includes(statusVal as (typeof validStatuses)[number])
          ? uiStatusToApi(statusVal as AnnouncementStatus)
          : undefined;
      const { dateFrom, dateTo } = parseDateRangeToISO(
        filterValues.dateRange as [Date | null, Date | null] | undefined
      );
      const response = await announcementsApi.getAll({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: statusApi,
        dateFrom,
        dateTo,
        sortBy,
        sortOrder,
      });
      setEntries(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setEntries([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filterValues, sortBy, sortOrder]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleRowSelect = (row: AnnouncementEntry) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(checked ? entries : []);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleCreateAnnouncement = () => {
    setEditId(null);
    setIsCreateDrawerOpen(true);
  };

  const buildAnnouncementBody = (
    data: AnnouncementFormValues,
    imageFileID: string | null | undefined,
    asDraft: boolean
  ) => {
    const title = data.title.trim().slice(0, 500);
    const startDate = data.startDate ? formDateToISOStart(data.startDate) : undefined;
    const endDate = data.endDate ? formDateToISOEnd(data.endDate) : undefined;
    const isAllStaff = data.targetAudience === TARGET_AUDIENCE_ALL;
    // Drawer Merkez/Taşra sentinel'larını çoklu UUID listesine çözer; varsa onu kullan,
    // yoksa eski tek-id akışına düş (targetAudience tek bir UUID'dir).
    const resolvedDepartmentIds =
      data.targetDepartmentIds && data.targetDepartmentIds.length > 0
        ? data.targetDepartmentIds
        : data.targetAudience
        ? [data.targetAudience]
        : undefined;
    const body = {
      title,
      description: data.description?.slice(0, 10000) ?? undefined,
      startDate,
      endDate,
      actionUrl: undefined as string | undefined,
      actionText: undefined as string | undefined,
      targetAllStaff: isAllStaff,
      targetDepartmentIds: isAllStaff ? undefined : resolvedDepartmentIds,
      targetEducationIds: undefined as string[] | undefined,
      status: (asDraft ? 0 : 1) as 0 | 1,
      displayType: data.displayType,
    };

    return imageFileID === undefined ? body : { ...body, imageFileID };
  };

  const handleCreateSubmit = async (
    data: AnnouncementFormValues,
    asDraft?: boolean,
    editContext?: EditAnnouncementContext
  ) => {
    try {
      const title = data.title.trim();
      if (!title) return;
      const imageFileID: string | null | undefined = editContext
        ? (data.imageFileID ?? null)
        : (data.imageFileID ?? undefined);
      // Geçmiş tarihli (endDate < bugün) duyuruyu yayınlanmış olarak
      // göndermek backend'in tüm hedef kullanıcılara bildirim
      // göndermesine yol açıyor — duyuru zaten süresi dolmuş haliyle
      // oluşturuluyor. Bu durumda hem kullanıcıyı uyar hem zorla taslak
      // olarak kaydet (backend tarafında bildirim tetiklenmesin diye).
      let isDraftEffective = asDraft ?? false;
      if (!isDraftEffective && data.endDate && !editContext) {
        const endTs = Date.parse(formDateToISOEnd(data.endDate));
        if (!Number.isNaN(endTs) && endTs < Date.now()) {
          // eslint-disable-next-line no-alert
          const ok = window.confirm(
            t("announcementsPage.datePastWarning") +
              "" +
              "bildirim tetiklenmeyecek). Devam etmek istiyor musunuz?",
          );
          if (!ok) return;
          isDraftEffective = true;
        }
      }
      const body = buildAnnouncementBody(data, imageFileID, isDraftEffective);
      if (editContext) {
        await announcementsApi.update(editContext.editId, body);
      } else {
        await announcementsApi.create(body);
      }
      fetchEntries();
    } catch (err) {
      console.error(
        editContext ? t("announcementsPage.updateError") : "Create announcement failed:",
        err
      );
    } finally {
      setIsCreateDrawerOpen(false);
      setEditId(null);
    }
  };

  const handleViewDetail = (row: AnnouncementEntry) => {
    handleDropdownToggle(row.id, false);
    setViewDrawerId(row.id);
  };

  const handleEdit = (row: AnnouncementEntry) => {
    handleDropdownToggle(row.id, false);
    setEditId(row.id);
    setIsCreateDrawerOpen(true);
  };

  const handleUnpublish = async (row: AnnouncementEntry) => {
    handleDropdownToggle(row.id, false);
    if (row.status !== "yayinda") return;
    try {
      await announcementsApi.unpublish(row.id);
      fetchEntries();
    } catch (err) {
      console.error("Unpublish failed:", err);
    }
  };

  const handleDeleteClick = (row: AnnouncementEntry) => {
    handleDropdownToggle(row.id, false);
    setDeleteTarget(row);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await announcementsApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      fetchEntries();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedRows.length === 0) return;
    setIsBulkDeleteConfirmOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await announcementsApi.bulkDelete(selectedRows.map((r) => r.id));
      setSelectedRows([]);
      setIsBulkDeleteConfirmOpen(false);
      fetchEntries();
    } catch (err) {
      console.error("Bulk delete failed:", err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkUnpublish = async () => {
    const toUnpublish = selectedRows.filter((r) => r.status === "yayinda");
    if (toUnpublish.length === 0) return;
    setIsBulkUnpublishing(true);
    try {
      await announcementsApi.bulkUnpublish(toUnpublish.map((r) => r.id));
      setSelectedRows([]);
      fetchEntries();
    } catch (err) {
      console.error("Bulk unpublish failed:", err);
    } finally {
      setIsBulkUnpublishing(false);
    }
  };

  const sortOptions: SortOption[] = [
    { label: t("announcements.sort.newest"), value: "createdAt-desc" },
    { label: t("announcements.sort.oldest"), value: "createdAt-asc" },
    {
      label: t("announcements.sort.titleAsc"),
      value: "title-asc",
    },
    {
      label: t("announcements.sort.titleDesc"),
      value: "title-desc",
    },
  ];

  const handleSortChange = (option: SortOption) => {
    const [by, order] = String(option.value ?? "createdAt-desc").split("-");
    setSortBy((by as "createdAt" | "startDate" | "title" | "status") || "createdAt");
    setSortOrder((order as "asc" | "desc") || "desc");
    setCurrentPage(1);
  };

  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("announcements.filters.status"),
      type: "dropdown",
      placeholder: t("announcements.filters.statusPlaceholder"),
      options: Object.entries(STATUS_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      name: "dateRange",
      label: t("announcements.filters.dateRange"),
      type: "daterange",
      placeholder: t("announcements.filters.datePlaceholder"),
    },
  ];

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(
      filters as Record<
        string,
        AnnouncementStatus | AnnouncementStatus[] | string | string[] | [Date | null, Date | null]
      >
    );
    setCurrentPage(1);
  };

  const columns: TableColumn<AnnouncementEntry>[] = [
    {
      header: t("announcements.columns.title"),
      accessor: (row) => (
        <div className={styles.titleCell}>
          <div className={styles.titleThumb}>
            {row.imageFileID ? (
              <FileImage
                fileId={row.imageFileID}
                alt=""
                width={44}
                height={44}
                className={styles.titleThumbImg}
              />
            ) : (
              <span className={styles.titleThumbPlaceholder} />
            )}
          </div>
          <span className={styles.titleText} title={row.title}>
            {row.title}
          </span>
        </div>
      ),
      className: "title-column",
    },
    {
      header: t("announcements.columns.content"),
      accessor: "content",
      className: "content-column",
    },
    {
      header: t("announcements.columns.targetAudience"),
      accessor: "targetAudience",
      className: "target-audience-column",
    },
    {
      header: t("announcements.columns.publishDate"),
      accessor: "publishDate",
      className: "publish-date-column",
    },
    {
      header: t("announcements.columns.status"),
      accessor: (row) => (
        <Chip
          type={STATUS_CHIP_TYPE[row.status]}
          variant="soft"
          size="small"
        >
          {STATUS_LABELS[row.status]}
        </Chip>
      ),
      className: "status-column",
    },
  ];

  const renderActions = (row: AnnouncementEntry) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        label: t("announcements.actions.view"),
        icon: <EyeIcon width={18} height={18} />,
        onClick: () => handleViewDetail(row),
      },
      canUpdate && {
        label: t("announcements.actions.edit"),
        icon: <EditIcon width={18} height={18} />,
        onClick: () => handleEdit(row),
      },
      ...(canUpdate && row.status === "yayinda"
        ? [
            {
              label: t("announcements.actions.unpublish"),
              icon: <CloseIcon width={18} height={18} />,
              onClick: () => handleUnpublish(row),
            },
          ]
        : []),
      canDelete && {
        label: t("announcements.actions.delete"),
        icon: <DeleteIcon width={18} height={18} />,
        onClick: () => handleDeleteClick(row),
      },
    ].filter(Boolean) as { label: string; icon: React.ReactNode; onClick: () => void }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            type="button"
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          type="button"
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("announcements.title")} />

      <CreateAnnouncementDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => {
          setIsCreateDrawerOpen(false);
          setEditId(null);
        }}
        editId={editId}
        onSubmit={handleCreateSubmit}
      />

      <AnnouncementDetailDrawer
        isOpen={!!viewDrawerId}
        onClose={() => setViewDrawerId(null)}
        announcementId={viewDrawerId}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t("announcements.deleteConfirmTitle")}
        message={
          deleteTarget
            ? t("announcements.deleteConfirmMessage", `"${deleteTarget.title}" duyurusunu silmek istediğinize emin misiniz?`)
            : ""
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={isBulkDeleteConfirmOpen}
        onClose={() => setIsBulkDeleteConfirmOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        title={t("announcements.bulkDeleteConfirmTitle")}
        message={t("announcements.bulkDeleteConfirmMessage", { count: selectedRows.length })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={isBulkDeleting}
        confirmVariant="danger"
      />

      <PageHeader
        searchPlaceholder={t("announcements.searchPlaceholder")}
        searchValue={searchInput}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={`${sortBy}-${sortOrder}`}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleCreateAnnouncement : undefined}
        addButtonLabel={canCreate ? t("announcements.addButton") : undefined}
      />

      {selectedRows.length > 0 && (canDelete || canUpdate) && (
        <div className={styles.bulkActionsBar}>
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon width={18} height={18} />}
              onClick={handleBulkDeleteClick}
              disabled={isBulkDeleting}
            >
              {t("announcements.bulkActions.deleteSelected")}
            </Button>
          )}
          {canUpdate && (
            <Button
              variant="outlined"
              color="default"
              className={styles.bulkUnpublishBtn}
              startIcon={<CloseIcon width={18} height={18} color="#666F82" />}
              onClick={handleBulkUnpublish}
              disabled={isBulkUnpublishing}
            >
              {t("announcements.bulkActions.unpublishSelected")}
            </Button>
          )}
        </div>
      )}

      <Table
        columns={columns}
        data={entries}
        isLoading={isLoading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("announcements.noData")}
      />
    </DashboardLayout>
  );
};

export default AnnouncementsPage;
